import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { BASE_URL } from '../apiConfig'

// Original fetchPDD - keep as is for backward compatibility
export const fetchPDD = createAsyncThunk(
  'pddData/fetchPDD',
  async ({ jwtBearer, movement }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${BASE_URL}pdd/get/pdd/data/?movement=${movement}`, {
        headers: {
          Authorization: `Bearer ${jwtBearer}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          window.location.reload();
        }
        const errorData = await response.json();
        throw new Error(errorData?.message || `HTTP error! Status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Fetch error:', error);
      return rejectWithValue(error.message);
    }
  }
);

// New V2 API with pagination and search
export const fetchPDDV2 = createAsyncThunk(
  'pddData/fetchPDDV2',
  async ({ jwtBearer, params = {} }, { rejectWithValue }) => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      
      // Add pagination params
      if (params.page) queryParams.append('page', params.page);
      if (params.page_size) queryParams.append('page_size', params.page_size);
      
      // Add search filters
      if (params.tracking_number) queryParams.append('tracking_number', params.tracking_number);
      if (params.application_id) queryParams.append('application_id', params.application_id);
      if (params.product) queryParams.append('product', params.product);
      
      const url = `${BASE_URL}pdd/get/pdd/data/v2/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${jwtBearer}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          window.location.reload();
        }
        const errorData = await response.json();
        throw new Error(errorData?.message || `HTTP error! Status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Fetch error:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Fetch kit data
export const fetchKitData = createAsyncThunk(
  'pddData/fetchKitData',
  async ({ jwtBearer, movement }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${BASE_URL}pdd/get/pdd/data/?movement=Kit_Report`, {
        headers: {
          Authorization: `Bearer ${jwtBearer}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          window.location.reload();
        }
        const errorData = await response.json();
        throw new Error(errorData?.message || `HTTP error! Status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Fetch error:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchTrackingDetails = createAsyncThunk(
  'pddData/fetchTrackingDetails',
  async ({ jwtBearer, tracking_number }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${BASE_URL}pdd/get/tracking/details/?tracking_number=${tracking_number}`,
        {
          headers: {
            Authorization: `Bearer ${jwtBearer}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          window.location.reload();
        }
        const errorData = await response.json();
        throw new Error(errorData?.error || `HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        return rejectWithValue(data.error || 'Failed to fetch tracking details');
      }

      return data.data;
    } catch (error) {
      console.error('Fetch tracking details error:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateCourierStatus = createAsyncThunk(
  'pddData/updateCourierStatus',
  async ({ jwtBearer, trackingData }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${BASE_URL}pdd/update/courier/status/`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${jwtBearer}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            tracking_number: trackingData.tracking_number,
            status: trackingData.status,
            application_ids: trackingData.app_id || trackingData.application_ids
          }),
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          window.location.reload();
        }
        const errorData = await response.json();
        throw new Error(errorData?.error || `HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        return rejectWithValue(data.error || 'Failed to update status');
      }

      return data.data;
    } catch (error) {
      console.error('Update status error:', error);
      return rejectWithValue(error.message);
    }
  }
);


export const downloadPddReport = createAsyncThunk(
  'pddData/downloadReport',
  async ({ type, fileName, jwtBearer }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${BASE_URL}pdd/download/reports/?type=${type}`, {
        headers: {
          Authorization: `Bearer ${jwtBearer}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          window.location.reload();
        }
        const errorData = await response.json();
        throw new Error(errorData?.detail || errorData?.message || `HTTP error! Status: ${response.status}`);
      }

      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileName}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success(`${fileName} downloaded successfully`);
      return { type, fileName, success: true };
    } catch (error) {
      console.error('Download report error:', error);
      message.error(`Failed to download ${fileName}: ${error.message}`);
      return rejectWithValue(error.message);
    }
  }
);

const pddDataSlice = createSlice({
    name: 'pddData',
    initialState: {
        // Original PDD data states
        isLoading: false,
        data: null,
        isError: false,
        
        // Kit data states
        isLoadingKit: false,
        dataKit: null,
        isErrorKit: false,
        
        // New V2 data states
        v2Loading: false,
        v2Data: null,
        v2Error: null,
        
        // For tracking details
        trackingDetails: null,
        trackingLoading: false,
        trackingError: null,
        
        // For status update
        updateStatusLoading: false,
        updateStatusError: null
    },
    reducers: {
        clearTrackingDetails: (state) => {
          state.trackingDetails = null;
          state.trackingError = null;
        },
        clearData: (state) => {
          state.data = null;
          state.isError = false;
          state.isLoading = false;
        },
        clearV2Data: (state) => {
          state.v2Data = null;
          state.v2Error = null;
          state.v2Loading = false;
        },
        clearKitData: (state) => {
          state.dataKit = null;
          state.isErrorKit = false;
          state.isLoadingKit = false;
        }
    },
    extraReducers: builder => {
      // Handle original fetchPDD
      builder.addCase(fetchPDD.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      builder.addCase(fetchPDD.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      builder.addCase(fetchPDD.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
      })

      // Handle fetchPDDV2
      builder.addCase(fetchPDDV2.pending, (state) => {
        state.v2Loading = true;
        state.v2Error = null;
      })
      builder.addCase(fetchPDDV2.fulfilled, (state, action) => {
        state.v2Loading = false;
        state.v2Data = action.payload;
        // console.log('V2 Data fetched successfully:', action.payload);
        
      })
      builder.addCase(fetchPDDV2.rejected, (state, action) => {
        state.v2Loading = false;
        state.v2Error = action.payload;
      })

      // Handle fetchKitData
      builder.addCase(fetchKitData.pending, (state) => {
        state.isLoadingKit = true;
        state.isErrorKit = false;
      })
      builder.addCase(fetchKitData.fulfilled, (state, action) => {
        state.isLoadingKit = false;
        state.dataKit = action.payload;
      })
      builder.addCase(fetchKitData.rejected, (state, action) => {
        state.isLoadingKit = false;
        state.isErrorKit = true;
      })

      // Handle tracking details
      builder.addCase(fetchTrackingDetails.pending, (state) => {
        state.trackingLoading = true;
        state.trackingError = null;
      });
      
      builder.addCase(fetchTrackingDetails.fulfilled, (state, action) => {
        state.trackingLoading = false;
        state.trackingDetails = action.payload;
      });
      
      builder.addCase(fetchTrackingDetails.rejected, (state, action) => {
        state.trackingLoading = false;
        state.trackingError = action.payload || 'Failed to fetch tracking details';
      });

      // Handle status update
      builder.addCase(updateCourierStatus.pending, (state) => {
        state.updateStatusLoading = true;
        state.updateStatusError = null;
      });
      
      builder.addCase(updateCourierStatus.fulfilled, (state, action) => {
        state.updateStatusLoading = false;
        // Update the tracking details with new status
        if (state.trackingDetails) {
          state.trackingDetails.status = "Received";
          if (state.trackingDetails.applications) {
            state.trackingDetails.applications.forEach(app => {
              app.status = "Received";
            });
          }
        }
        // Also update in V2 data list if exists
        if (state.v2Data?.data) {
          const index = state.v2Data.data.findIndex(item => 
            item.tracking_number === action.payload?.tracking_number
          );
          if (index !== -1) {
            state.v2Data.data[index].status = "3";
          }
        }
        // Also update in original data list if exists
        if (state.data?.data) {
          const index = state.data.data.findIndex(item => 
            item.tracking_number === action.payload?.tracking_number
          );
          if (index !== -1) {
            state.data.data[index].status = "3";
          }
        }
      });
      
      builder.addCase(updateCourierStatus.rejected, (state, action) => {
        state.updateStatusLoading = false;
        state.updateStatusError = action.payload || 'Failed to update status';
      });
    }
})

export const { 
  clearTrackingDetails, 
  clearData, 
  clearV2Data, 
  clearKitData 
} = pddDataSlice.actions;
export default pddDataSlice.reducer;